import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, Lightbulb, Shield } from "lucide-react";

const values = [
  { icon: Target, title: "Precision", description: "Every line of code serves a purpose. We build systems with intentionality." },
  { icon: Users, title: "Partnership", description: "We work alongside our clients as technical partners, not vendors." },
  { icon: Lightbulb, title: "Innovation", description: "We explore emerging technologies and apply them where they create value." },
  { icon: Shield, title: "Reliability", description: "Systems we build are designed to operate under pressure." }
];

const process = [
  { step: "01", title: "Discovery", desc: "Understanding requirements and constraints" },
  { step: "02", title: "Design", desc: "Architecture and technical specification" },
  { step: "03", title: "Development", desc: "Iterative implementation with feedback" },
  { step: "04", title: "Delivery", desc: "Deployment, documentation, knowledge transfer" }
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-6 opacity-0 animate-fade-up">
              About
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Building the
              <br />
              <span className="text-mint">infrastructure</span>
              <br />
              for digital.
            </h1>
            <p className="mt-10 text-xl text-slate-400 leading-relaxed max-w-2xl opacity-0 animate-fade-up animate-delay-200">
              Raindas Technologies is a software engineering company focused on building products and delivering custom solutions for organizations that require high-performance digital systems.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-obsidian-light" data-testid="about-story">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                Our Story
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
                Founded on
                <br />
                engineering
                <br />
                <span className="text-outline">principles</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-8">
              <p className="text-slate-300 text-xl leading-relaxed">
                Raindas was established with a clear focus: build software systems that organizations can rely on.
              </p>
              <p className="text-slate-400 text-lg leading-relaxed">
                Our team brings experience from enterprise software, financial services, and cloud infrastructure. This background shapes our approach to system design, where security, scalability, and maintainability are foundational requirements.
              </p>
              <p className="text-slate-400 text-lg leading-relaxed">
                Today, we operate across two primary areas: developing proprietary software products and delivering custom engineering solutions through Raindas Studio.
              </p>
            </div>
          </div>
        </div>

        {/* Team Image */}
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 mt-24">
          <div className="relative overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?q=80&w=3270&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Team collaboration"
              className="w-full h-[400px] object-cover img-grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian-light via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="about-values">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-electric mb-4">
              Values
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
              What guides our work
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, idx) => (
              <div
                key={value.title}
                className="card-beam p-10 group"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <value.icon className="w-10 h-10 text-mint mb-8 group-hover:text-coral transition-colors" />
                <h3 className="text-2xl font-heading font-bold text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-slate-400 text-lg leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-obsidian-light" data-testid="about-approach">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                Approach
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
                How we work
              </h2>
              <p className="mt-8 text-slate-300 text-xl leading-relaxed">
                Every engagement begins with understanding the problem. We invest time in requirements analysis and system design before writing code.
              </p>
              <p className="mt-6 text-slate-400 leading-relaxed">
                Our development process emphasizes continuous integration, automated testing, and incremental delivery. This approach reduces risk while maintaining visibility throughout the project lifecycle.
              </p>
            </div>
            <div className="space-y-4">
              {process.map((item, idx) => (
                <div 
                  key={item.step} 
                  className="flex items-start gap-6 p-6 bg-white/5 border border-white/5 hover:border-white/10 transition-colors"
                >
                  <span className="text-4xl font-heading font-bold text-mint">
                    {item.step}
                  </span>
                  <div>
                    <h3 className="text-white font-bold text-lg">{item.title}</h3>
                    <p className="text-slate-400 text-sm mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
            Want to join the team?
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            We're always looking for talented engineers.
          </p>
          <Link to="/careers">
            <button className="mt-10 btn-primary group flex items-center gap-3 bg-mint text-black px-8 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors mx-auto">
              View Open Positions
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
